// src/pages/ChatBot/query-logs/inspect-trace.tsx
//
// Retrieval-trace explorer for the Inspect modal: a stage-lens segmented
// control re-ranks the chunk cards through any pipeline stage, each card
// showing a normalized score bar, a V/T/F/R/# rank trajectory, and an
// expandable chunk text. All math lives in inspect-trace-helpers.ts.

import { useMemo, useState } from 'react';
import type { ChunkTrace } from './types';
import { LOW_CONFIDENCE_RERANK_SCORE } from './derive';
import { termMatchesToken } from './lexical';
import {
  CHUNK_UNAVAILABLE_TEXT,
  RERANK_SCALE_MAX,
  TRACE_STAGES,
  buildMatchedTermsByChunk,
  buildRankTrajectory,
  countChunksAtStage,
  formatStageScore,
  getDefaultStage,
  getFoundBy,
  getStageMaxScore,
  getStageMeta,
  getStageRank,
  getStageScore,
  normalizeScoreFraction,
  partitionChunksForStage,
} from './inspect-trace-helpers';
import type {
  FoundBy,
  ResolvedSearchTerms,
  StageMeta,
  TraceSortMode,
  TraceStage,
} from './inspect-trace-helpers';

// Human labels for the per-chunk retrieval-origin badge.
const FOUND_BY_LABEL: Record<FoundBy, string> = {
  vector: 'vector',
  keyword: 'keyword',
  both: 'both',
};

const LOW_CONFIDENCE_TICK_PERCENT = (LOW_CONFIDENCE_RERANK_SCORE / RERANK_SCALE_MAX) * 100;

interface TraceExplorerProps {
  chunks: ChunkTrace[];
  chunkTexts: Record<string, string>;
  searchTerms: ResolvedSearchTerms;
}

export default function TraceExplorer({ chunks, chunkTexts, searchTerms }: TraceExplorerProps) {
  const [activeStage, setActiveStage] = useState<TraceStage>(() => getDefaultStage(chunks));
  const [sortMode, setSortMode] = useState<TraceSortMode>('rank');
  const [expandedChunkIds, setExpandedChunkIds] = useState<ReadonlySet<string>>(new Set());

  const activeStageMeta = getStageMeta(activeStage);
  const isTextLens = activeStage === 'text';
  // The text axis is rank-only, so matched-term counts stand in for its score:
  // they drive the coverage bar, the chips, and the "by matches" sort.
  const canSortByMatches = isTextLens && searchTerms.terms.length > 0;

  const matchedTermsByChunk = useMemo(
    () => buildMatchedTermsByChunk(chunks, chunkTexts, searchTerms.terms),
    [chunks, chunkTexts, searchTerms],
  );
  const coverageByChunk = useMemo(() => {
    const counts = new Map<string, number>();
    matchedTermsByChunk.forEach((terms, chunkId) => counts.set(chunkId, terms.length));
    return counts;
  }, [matchedTermsByChunk]);

  const { present, absent } = useMemo(
    () => partitionChunksForStage(chunks, activeStage, sortMode, coverageByChunk),
    [chunks, activeStage, sortMode, coverageByChunk],
  );
  const stageMaxScore = useMemo(
    () => getStageMaxScore(chunks, activeStage),
    [chunks, activeStage],
  );

  const handleStageChange = (stage: TraceStage) => {
    setActiveStage(stage);
    setSortMode('rank');
  };

  const toggleChunkExpanded = (chunkId: string) => {
    setExpandedChunkIds((current) => {
      const next = new Set(current);
      if (next.has(chunkId)) next.delete(chunkId);
      else next.add(chunkId);
      return next;
    });
  };

  const renderCard = (chunk: ChunkTrace, isAbsent: boolean) => (
    <ChunkCard
      key={chunk.chunkId}
      chunk={chunk}
      stageMeta={activeStageMeta}
      stageMaxScore={stageMaxScore}
      chunkText={chunkTexts[chunk.chunkId]}
      matchedTerms={matchedTermsByChunk.get(chunk.chunkId)}
      totalTerms={searchTerms.terms.length}
      isAbsent={isAbsent}
      isExpanded={expandedChunkIds.has(chunk.chunkId)}
      onToggle={() => toggleChunkExpanded(chunk.chunkId)}
    />
  );

  return (
    <div className="qli-trace">
      <div className="qli-trace-controls">
        <div className="qli-stage-lens" role="group" aria-label="Retrieval stage lens">
          {TRACE_STAGES.map((meta) => (
            <button
              key={meta.stage}
              type="button"
              className={`qli-stage-btn${meta.stage === activeStage ? ' qli-stage-btn-active' : ''}`}
              aria-pressed={meta.stage === activeStage}
              onClick={() => handleStageChange(meta.stage)}
            >
              {meta.label}
              <span className="qli-stage-count">{countChunksAtStage(chunks, meta.stage)}</span>
            </button>
          ))}
        </div>
        {(activeStageMeta.hasScore || canSortByMatches) && (
          <div className="qli-sort-toggle" role="group" aria-label="Sort order">
            {(['rank', 'score'] as TraceSortMode[]).map((mode) => (
              <button
                key={mode}
                type="button"
                className={`qli-sort-btn${mode === sortMode ? ' qli-sort-btn-active' : ''}`}
                aria-pressed={mode === sortMode}
                onClick={() => setSortMode(mode)}
              >
                by {mode === 'score' && isTextLens ? 'matches' : mode}
              </button>
            ))}
          </div>
        )}
      </div>

      {isTextLens && searchTerms.terms.length > 0 && (
        <div className="qli-term-strip">
          <span className="qli-micro-label">search terms ({searchTerms.terms.length})</span>
          {searchTerms.terms.map((term) => (
            <span key={term} className="qli-term-chip">
              {term}
            </span>
          ))}
          {searchTerms.derived && (
            <span className="qli-term-strip-note">derived from the standalone query</span>
          )}
        </div>
      )}

      <div className="qli-chunk-list">{present.map((chunk) => renderCard(chunk, false))}</div>

      {absent.length > 0 && (
        <div className="qli-absent-bucket">
          <div className="qli-micro-label">not present at this stage</div>
          {absent.map((chunk) => renderCard(chunk, true))}
        </div>
      )}

      <div className="qli-trace-legend">
        Vector = cosine · Text = keyword rank + matched query terms (Cosmos returns no BM25 score) ·
        Fused = RRF · Rerank = LLM judge 0–100 (&lt;
        {LOW_CONFIDENCE_RERANK_SCORE} low confidence) · # = served order
      </div>
    </div>
  );
}

interface ChunkCardProps {
  chunk: ChunkTrace;
  stageMeta: StageMeta;
  stageMaxScore: number | undefined;
  chunkText: string | undefined;
  matchedTerms: string[] | undefined;   // undefined = chunk text unavailable, matches unknowable
  totalTerms: number;
  isAbsent: boolean;
  isExpanded: boolean;
  onToggle: () => void;
}

function ChunkCard({
  chunk,
  stageMeta,
  stageMaxScore,
  chunkText,
  matchedTerms,
  totalTerms,
  isAbsent,
  isExpanded,
  onToggle,
}: ChunkCardProps) {
  const stageRank = getStageRank(chunk, stageMeta.stage);
  const trajectory = buildRankTrajectory(chunk);
  const foundBy = getFoundBy(chunk);
  const isTextLens = stageMeta.stage === 'text';
  const isChunkTextUnavailable = chunkText === undefined || chunkText === CHUNK_UNAVAILABLE_TEXT;
  const shouldHighlightText =
    isTextLens && !isChunkTextUnavailable && matchedTerms !== undefined && matchedTerms.length > 0;

  return (
    <div className={`qli-chunk-card${isAbsent ? ' qli-chunk-card-absent' : ''}`}>
      <button type="button" className="qli-chunk-head" aria-expanded={isExpanded} onClick={onToggle}>
        <span className="qli-rank-badge">{stageRank !== undefined ? `#${stageRank}` : '–'}</span>
        <span className="qli-chunk-main">
          <span className="qli-chunk-title-row">
            <span className="qli-chunk-doc">{chunk.documentName}</span>
            {foundBy && (
              <span
                className={`qli-found-by qli-found-by-${foundBy}`}
                title={`Retrieved by the ${FOUND_BY_LABEL[foundBy]} search axis`}
              >
                found by: {FOUND_BY_LABEL[foundBy]}
              </span>
            )}
            {chunk.sectionHeader && (
              <span className="qli-chunk-muted"> · {chunk.sectionHeader}</span>
            )}
            {chunk.pageNumber !== undefined && (
              <span className="qli-chunk-muted"> · page {chunk.pageNumber}</span>
            )}
            {chunk.citationMarker !== undefined && (
              <span
                className="qli-cite-badge"
                title={`Cited in the answer as [${chunk.citationMarker}]`}
              >
                [{chunk.citationMarker}]
              </span>
            )}
          </span>
          {isTextLens && stageRank !== undefined && (
            <span className="qli-keyword-rank">#{stageRank} in keyword results</span>
          )}
          {isTextLens && totalTerms > 0 && (
            <KeywordMatchRow matchedTerms={matchedTerms} totalTerms={totalTerms} />
          )}
          {stageMeta.hasScore && (
            <ScoreBar
              score={getStageScore(chunk, stageMeta.stage)}
              stage={stageMeta.stage}
              stageMaxScore={stageMaxScore}
            />
          )}
        </span>
        <span className="qli-trajectory" aria-label="Rank trajectory across stages">
          {trajectory.map((cell) => (
            <span key={cell.stage} className="qli-traj-cell" title={cell.title}>
              <span className="qli-traj-label">{cell.shortLabel}</span>
              <span className={`qli-traj-rank qli-traj-${cell.direction}`}>
                {cell.rank !== undefined ? cell.rank : '–'}
              </span>
            </span>
          ))}
        </span>
      </button>
      {isExpanded && (
        <div className={`qli-chunk-text${isChunkTextUnavailable ? ' qli-chunk-text-missing' : ''}`}>
          {isChunkTextUnavailable ? (
            CHUNK_UNAVAILABLE_TEXT
          ) : shouldHighlightText ? (
            <HighlightedChunkText text={chunkText} matchedTerms={matchedTerms} />
          ) : (
            chunkText
          )}
        </div>
      )}
    </div>
  );
}

// The per-card keyword-overlap readout on the text lens: coverage bar + matched-term
// chips, an explicit "no keyword overlap" state (a text rank with zero overlap is a
// false-positive signal worth spotting), or "unknown" when the chunk text is gone.
function KeywordMatchRow({
  matchedTerms,
  totalTerms,
}: {
  matchedTerms: string[] | undefined;
  totalTerms: number;
}) {
  if (matchedTerms === undefined) {
    return (
      <span className="qli-match-unknown">chunk text unavailable — keyword matches unknown</span>
    );
  }
  if (matchedTerms.length === 0) {
    return <span className="qli-no-overlap">no keyword overlap</span>;
  }
  return (
    <span className="qli-keyword-coverage">
      <span className="qli-coverage-track">
        <span
          className="qli-coverage-fill"
          style={{ width: `${(matchedTerms.length / totalTerms) * 100}%` }}
        />
      </span>
      <span className="qli-coverage-value">
        {matchedTerms.length}/{totalTerms} terms
      </span>
      {matchedTerms.map((term) => (
        <span key={term} className="qli-term-chip">
          {term}
        </span>
      ))}
    </span>
  );
}

// Expanded chunk text with matched tokens wrapped in <mark>. The split regex keeps
// alphanumeric token runs at odd indices, so matching uses the exact same
// termMatchesToken rule as the chips — whole tokens only, never substrings.
function HighlightedChunkText({ text, matchedTerms }: { text: string; matchedTerms: string[] }) {
  const parts = useMemo(() => text.split(/([a-zA-Z0-9]+)/), [text]);
  return (
    <>
      {parts.map((part, index) => {
        const isToken = index % 2 === 1;
        if (!isToken) return part;
        const tokenLower = part.toLowerCase();
        const isHit = matchedTerms.some((term) => termMatchesToken(term, tokenLower));
        return isHit ? (
          <mark key={index} className="qli-term-hit">
            {part}
          </mark>
        ) : (
          part
        );
      })}
    </>
  );
}

interface ScoreBarProps {
  score: number | undefined;
  stage: TraceStage;
  stageMaxScore: number | undefined;
}

function ScoreBar({ score, stage, stageMaxScore }: ScoreBarProps) {
  const fillPercent =
    score !== undefined ? normalizeScoreFraction(score, stage, stageMaxScore) * 100 : 0;

  return (
    <span className="qli-score-row">
      <span className="qli-score-track">
        {score !== undefined && (
          <span
            className={`qli-score-fill qli-score-fill-${stage}`}
            style={{ width: `${fillPercent}%` }}
          />
        )}
        {stage === 'rerank' && (
          <span
            className="qli-score-tick"
            style={{ left: `${LOW_CONFIDENCE_TICK_PERCENT}%` }}
            title={`Low-confidence threshold (${LOW_CONFIDENCE_RERANK_SCORE})`}
          />
        )}
      </span>
      <span className="qli-score-value">
        {score !== undefined ? formatStageScore(score, stage) : '–'}
      </span>
    </span>
  );
}
